import { Phone, Mail, CalendarCheck, Clock, CreditCard, MessageSquareText, MessageCircle } from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, BOOKING_URL, SERVICES, SMS_HREF, WHATSAPP_HREF, TEXT_NUMBER } from "@/data/site";
import { LeadForm } from "@/components/LeadForm";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { EstimateForm } from "@/components/EstimateForm";
import { PayLaterBanner } from "@/components/PayLaterBanner";
import { WarrantyBlock } from "@/components/WarrantyBlock";
import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Seo } from "@/components/Seo";

const ContactMethodCards = () => (
  <>
    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-book-card"
      className="block bg-brand text-white p-8 rounded-sm hover:bg-brand-dark transition-colors duration-200">
      <CalendarCheck className="w-8 h-8 mb-4" />
      <h2 className="font-heading font-bold text-2xl">Book Service Online</h2>
      <p className="text-blue-100 text-sm mt-2">Fastest option. Pick your service and time slot on our secure PayPal booking page.</p>
      <span className="inline-block mt-4 bg-brand-ice text-brand-ink font-bold px-6 py-3 rounded-sm">Open Booking Page</span>
    </a>
    <div data-testid="contact-call-card" className="bg-white border border-zinc-200 p-8 rounded-sm hover:border-brand transition-colors duration-200">
      <Phone className="w-8 h-8 text-brand mb-4" />
      <a href={PHONE_HREF} data-testid="contact-call-link" className="font-heading font-bold text-2xl text-brand-ink hover:text-brand transition-colors duration-200">{PHONE}</a>
      <div className="flex flex-wrap gap-2 mt-3">
        <a href={SMS_HREF} data-testid="contact-text-btn"
          className="inline-flex items-center gap-1.5 bg-brand-subtle text-brand-ink text-sm font-bold px-4 py-2 rounded-sm border border-zinc-200 hover:border-brand transition-colors duration-200">
          <MessageSquareText className="w-4 h-4 text-brand" /> Text {TEXT_NUMBER}
        </a>
        <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
          className="inline-flex items-center gap-1.5 bg-[#25D366] text-white text-sm font-bold px-4 py-2 rounded-sm hover:bg-[#1EBE5A] transition-colors duration-200">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
      </div>
      <p className="text-zinc-600 text-sm mt-3 flex items-center gap-1.5"><Clock className="w-4 h-4 shrink-0" /> 24/7 emergency booking. Call any time — our system takes your request, and a technician is dispatched after a $200 prepaid emergency booking fee, which is applied toward your repair.</p>
    </div>
    <a href={EMAIL_HREF} data-testid="contact-email-card"
      className="block bg-white border border-zinc-200 p-8 rounded-sm hover:border-brand transition-colors duration-200">
      <Mail className="w-8 h-8 text-brand mb-4" />
      <h2 className="font-heading font-bold text-2xl text-brand-ink">{EMAIL}</h2>
      <p className="text-zinc-600 text-sm mt-2">For quotes, documents and non-urgent questions.</p>
    </a>
  </>
);

const PayOnlineCard = ({ payItem, setPayItem, payable }) => (
  <div data-testid="pay-online-card" className="bg-white border border-zinc-200 p-8 rounded-sm">
    <CreditCard className="w-8 h-8 text-brand mb-4" />
    <h2 className="font-heading font-bold text-2xl text-brand-ink">Pay Online</h2>
    <p className="text-zinc-600 text-sm mt-2 mb-4">Pay for a fixed-price service securely with PayPal — we'll call to schedule right after.</p>
    <Select value={payItem} onValueChange={setPayItem}>
      <SelectTrigger data-testid="pay-item-select" className="rounded-sm mb-4"><SelectValue placeholder="Choose a service" /></SelectTrigger>
      <SelectContent>
        {payable.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`pay-item-${s.id}`}>
            {s.name} — {s.price} {s.unit}
          </SelectItem>
        ))}
        <SelectItem value="maintenance-annual" data-testid="pay-item-maintenance-annual">Annual Maintenance Plan — $249/yr</SelectItem>
        <SelectItem value="maintenance-monthly" data-testid="pay-item-maintenance-monthly">Maintenance Plan First Month — $24.99</SelectItem>
      </SelectContent>
    </Select>
    <PayPalCheckout itemId={payItem} />
  </div>
);

export default function Contact() {
  const [payItem, setPayItem] = useState("diagnostic");
  const payable = SERVICES.filter((s) => !s.quote);
  return (
    <div data-testid="contact-page">
      <Seo title="Contact Us — Book AC Service Today | Central Air Depot" description="Call 786-512-1929, book online in under a minute or request a callback. 24/7 emergency booking across South Florida." />
      <section className="bg-brand-ink text-white">
        <div className="max-w-7xl mx-auto px-4 py-20 sm:py-28">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-3">Contact &amp; Booking</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-black max-w-2xl">Get AC help today</h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-4 max-w-xl">Book online in under a minute, call us any time, or send a request and we'll call you back.</p>
        </div>
      </section>

      <section className="bg-brand-subtle">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            <ContactMethodCards />
            <PayOnlineCard payItem={payItem} setPayItem={setPayItem} payable={payable} />
          </div>
          <div className="lg:col-span-3">
            <LeadForm />
          </div>
        </div>
      </section>
      <section className="bg-white border-t border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-24 space-y-6">
          <EstimateForm />
          <PayLaterBanner />
        </div>
      </section>
      <WarrantyBlock />
    </div>
  );
}
